/**
 * Build planes: adding, editing, selecting, and the round trip to the engine.
 *
 * The engine handle is bound here by the engine bridge rather than imported,
 * so the bridge can wire the gizmo's callbacks to this module without the two
 * importing each other.
 */

import type { Engine } from "../../engine/Engine.ts";
import type { BuildPlane, PlaneGizmoMode, PlanePatch } from "../../engine/planes.ts";
import { setHud } from "../hud/hud.actions.ts";
import { planesState } from "./planes.state.ts";

/** A new plane's size, metres. */
const DEFAULT_WIDTH = 6;
const DEFAULT_DEPTH = 4;
const DEFAULT_OPACITY = 60;

/** Smallest side a plane may be resized to, metres. */
export const MIN_PLANE_SIZE = 0.5;

/** Fields are kept to millimetre precision, degrees to a tenth. */
const POSITION_DECIMALS = 3;
const ANGLE_DECIMALS = 1;

let engine: Engine | null = null;

export function bindPlanesEngine(bound: Engine): void {
  engine = bound;
  pushToEngine(false);
}

/** Mirror the store into the scene; `resolve` also refreshes colliders and re-solves the legs. */
function pushToEngine(resolve: boolean): void {
  if (!engine) {
    return;
  }

  const { planes, selectedId, mode, snap } = planesState.get();
  engine.planes.sync(planes, selectedId);
  engine.planes.setMode(mode);
  engine.planes.setSnap(snap);
  if (resolve) {
    engine.afterPlanesChange();
  }
}

function round(value: number, decimals: number): number {
  const factor = 10 ** decimals;

  return Math.round(value * factor) / factor;
}

function normaliseAngle(degrees: number): number {
  let angle = degrees % 360;
  if (angle > 180) {
    angle -= 360;
  }
  if (angle <= -180) {
    angle += 360;
  }

  return round(angle, ANGLE_DECIMALS);
}

/** Bring a plane onto the grid the fields use and keep it a usable size. */
function tidy(plane: BuildPlane): BuildPlane {
  return {
    ...plane,
    x: round(plane.x, POSITION_DECIMALS),
    y: round(plane.y, POSITION_DECIMALS),
    z: round(plane.z, POSITION_DECIMALS),
    w: Math.max(MIN_PLANE_SIZE, round(Math.abs(plane.w), POSITION_DECIMALS)),
    d: Math.max(MIN_PLANE_SIZE, round(Math.abs(plane.d), POSITION_DECIMALS)),
    rot: normaliseAngle(plane.rot),
    opacity: Math.max(0, Math.min(100, Math.round(plane.opacity))),
  };
}

function nextName(planes: readonly BuildPlane[]): string {
  let n = planes.length + 1;
  while (planes.some((plane) => plane.name === `Plane ${n}`)) {
    n++;
  }

  return `Plane ${n}`;
}

/** Add a plane at the camera's orbit target, so it appears in view, and select it. */
export function addPlane(): BuildPlane {
  const { planes } = planesState.get();
  const target = engine?.viewTarget() ?? { x: 0, y: 0, z: 0 };
  const plane: BuildPlane = {
    id: crypto.randomUUID(),
    name: nextName(planes),
    x: round(target.x, 1),
    y: Math.max(0, round(target.y, 2)),
    z: round(target.z, 1),
    w: DEFAULT_WIDTH,
    d: DEFAULT_DEPTH,
    rot: 0,
    opacity: DEFAULT_OPACITY,
    visible: true,
    collide: true,
  };

  planesState.set({ planes: [...planes, plane], selectedId: plane.id });
  pushToEngine(true);
  setHud(
    `${plane.name} added at ${plane.x}, ${plane.y}, ${plane.z} — drag the gizmo, or edit it in the Planes panel`,
  );

  return plane;
}

export function removePlane(id: string): void {
  const { planes, selectedId } = planesState.get();
  const plane = planes.find((entry) => entry.id === id);
  if (!plane) {
    return;
  }

  planesState.set({
    planes: planes.filter((entry) => entry.id !== id),
    selectedId: selectedId === id ? null : selectedId,
  });
  pushToEngine(true);
  setHud(`${plane.name} removed`);
}

/** Panel edits: any field, then the scene follows. */
export function updatePlane(id: string, patch: Partial<Omit<BuildPlane, "id">>): void {
  const { planes } = planesState.get();

  planesState.set({
    planes: planes.map((plane) => (plane.id === id ? tidy({ ...plane, ...patch }) : plane)),
  });
  pushToEngine(true);
}

/**
 * Gizmo edits: the scene already shows them, so only the store moves until the
 * drag ends — then the values are tidied and everything re-solves.
 */
export function applyViewportPlaneEdit(id: string, patch: PlanePatch, done: boolean): void {
  const { planes } = planesState.get();

  planesState.set({
    planes: planes.map((plane) => {
      if (plane.id !== id) {
        return plane;
      }

      return done ? tidy({ ...plane, ...patch }) : { ...plane, ...patch };
    }),
  });
  if (done) {
    pushToEngine(true);
  }
}

export function selectPlane(id: string | null): void {
  if (planesState.get().selectedId === id) {
    return;
  }

  planesState.set({ selectedId: id });
  pushToEngine(false);
}

export function setPlaneMode(mode: PlaneGizmoMode): void {
  planesState.set({ mode });
  pushToEngine(false);
}

export function setPlaneSnap(snap: boolean): void {
  planesState.set({ snap });
  pushToEngine(false);
}

export function framePlane(id: string): void {
  if (!engine?.framePlane(id)) {
    setHud("No such plane");
  }
}

/* -----------------------------------------------------------------------------
   Design JSON
   ----------------------------------------------------------------------------- */

export function serializePlanes(): readonly BuildPlane[] {
  return planesState.get().planes;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isBuildPlane(value: unknown): value is BuildPlane {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const plane: Record<string, unknown> = { ...value };

  return (
    typeof plane.id === "string" &&
    typeof plane.name === "string" &&
    ["x", "y", "z", "w", "d", "rot", "opacity"].every((key) => isFiniteNumber(plane[key])) &&
    typeof plane.visible === "boolean" &&
    typeof plane.collide === "boolean"
  );
}

/** Replace the planes with those of a loaded design; a design without any has none. */
export function restorePlanes(raw: unknown): void {
  const planes = Array.isArray(raw) ? raw.filter(isBuildPlane).map(tidy) : [];

  planesState.set({ planes, selectedId: null });
  pushToEngine(true);
}
